import type {
  Intervention as SolverIntervention,
  InterventionSessionTypeEnum,
  SchedulingInput,
  SchedulingJobResultResponse,
  SchedulingResult,
  Staff,
  StaffOccupancy,
  StaffProfessionResponse
} from '../api/generated';
import { SatisfactionIssueCriterionGapEnum } from '../api/generated';
import type { SolverTimeWindow } from '../types/schedulingResultTypes';
import type {
  Intervention,
  PersonDayLoad,
  PlannedWeekData,
  SessionType,
  Skill,
  UnplannedWeekData,
  UnscheduledIntervention,
  WeekKpiSummary,
  WeekShell,
  Weekday
} from '../types/weekOverview';
import { DEFAULT_OPENING_WEEKDAYS, WEEKDAYS_ORDER } from '../constants/weekOverviewConstants';
import { getDateForWeekday, getIsoWeekStartDate } from '../utils/weekDateUtils';

const MS_PER_DAY = 86_400_000;
const MAX_UTILIZATION_PERCENT = 100;

// Plan id the solver's PatientLunchSynthesizer stamps on the lunch breaks it
// injects for every patient (LUNCH_PLAN_ID in the algorithm project). Those
// arrive as INTERNAL sessions but should render as lunch, not meetings.
const LUNCH_PLAN_ID = 'patient-lunch-break';

// The solver only distinguishes three session kinds. The richer UI palette
// (COURSE, ASSESSMENT, ...) has no counterpart in its output, so INTERNAL
// sessions render as meetings and the other two map one-to-one.
const SESSION_TYPE_BY_SOLVER_TYPE: Record<InterventionSessionTypeEnum, SessionType> = {
  INDIVIDUAL: 'INDIVIDUAL',
  GROUP: 'GROUP',
  INTERNAL: 'MEETING'
};

function sessionTypeFor(definition: SolverIntervention): SessionType | null {
  if (definition.planId === LUNCH_PLAN_ID) return 'LUNCH';
  return definition.sessionType ? SESSION_TYPE_BY_SOLVER_TYPE[definition.sessionType] : null;
}

function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
}

function toIsoDateKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function roundToTenth(value: number): number {
  return Number(value.toFixed(1));
}

// A staff member's working window for one date. The generated TimeWindow type
// has the wrong field names for the solver payload (an OpenAPI schema-name
// collision — see SolverTimeWindow), so the runtime shape is cast here.
function workingWindowFor(
  workingHours: Record<string, object> | undefined,
  date: string
): SolverTimeWindow | undefined {
  return workingHours?.[date] as SolverTimeWindow | undefined;
}

function windowDurationHours(window: SolverTimeWindow): number {
  return (parseTimeToMinutes(window.endTime) - parseTimeToMinutes(window.startTime)) / 60;
}

// A staff member is occupied from preparation through post-processing; the
// solver omits the outer times when an intervention needs no prep/wrap-up.
function occupancyMinutes(occupancy: StaffOccupancy): number | null {
  const start = occupancy.preparationStart ?? occupancy.interventionStart;
  const end = occupancy.postProcessingEnd ?? occupancy.interventionEnd;
  if (!start || !end) return null;
  return parseTimeToMinutes(end) - parseTimeToMinutes(start);
}

function buildWeekShell(year: number, week: number, locationName: string): WeekShell {
  const weekStart = getIsoWeekStartDate(year, week);
  const weekEnd = new Date(weekStart.getTime() + 6 * MS_PER_DAY);
  return {
    weekNumber: week,
    year,
    weekStartDate: weekStart.toISOString(),
    weekEndDate: weekEnd.toISOString(),
    locationName,
    skills: [],
    staff: [],
    rooms: [],
    patients: []
  };
}

// A week without a completed calculation: the header renders from the shell
// and the page shows the empty state based on `isPlanned`.
export function buildUnplannedWeekData(
  year: number,
  week: number,
  locationName: string
): UnplannedWeekData {
  return { ...buildWeekShell(year, week, locationName), isPlanned: false };
}

// The competences the heatmap must list even when nothing in the solver result
// mentions them. The list endpoint's contract does not promise an archived-free
// register, so filter here rather than trusting the caller's slice to be clean.
function kpiFlaggedProfessionIds(professions: StaffProfessionResponse[]): string[] {
  return professions
    .filter((profession) => profession.showInKpi && !profession.archived)
    .map((profession) => profession.id);
}

// Every profession the solver result actually mentions: held by someone on the
// roster, or required by one of the interventions.
function referencedProfessionIds(input: SchedulingInput): string[] {
  const professionIds: string[] = [];
  for (const staff of input.staff ?? []) {
    for (const professionId of staff.staffProfessionIds ?? []) professionIds.push(professionId);
  }
  for (const intervention of input.interventions ?? []) {
    for (const professionId of requiredSkillIdsFor(intervention)) professionIds.push(professionId);
  }
  return professionIds;
}

// Every competence flagged "Show in KPI", plus every profession referenced by
// the roster or an intervention requirement. The KPI-flagged ones are listed
// even when nobody holds them and nothing requires them, so the heatmap reports
// them as 0% rather than hiding them. Names come from the staff-profession
// register, falling back to the raw id so an unknown profession still renders
// instead of disappearing from the heatmap.
function buildSkills(input: SchedulingInput, professions: StaffProfessionResponse[]): Skill[] {
  const skillIds = new Set<string>([
    ...kpiFlaggedProfessionIds(professions),
    ...referencedProfessionIds(input)
  ]);

  const nameById = new Map(professions.map((profession) => [profession.id, profession.name]));
  return [...skillIds]
    .map((id) => ({ id, name: nameById.get(id) ?? id }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function requiredSkillIdsFor(intervention: SolverIntervention): string[] {
  const skillIds = new Set<string>();
  for (const requirement of intervention.mandatoryStaffCompetenceRequirements ?? []) {
    for (const professionId of requirement.staffProfessionIds ?? []) skillIds.add(professionId);
  }
  for (const requirement of intervention.flexStaffCompetenceRequirements ?? []) {
    if (requirement.staffProfessionId) skillIds.add(requirement.staffProfessionId);
  }
  return [...skillIds];
}

// A calendar block's id. One intervention definition can be planned several
// times in a week, so the assignment's slot and its position in the plan are
// folded in: the id identifies the occurrence, not the definition. The calendar
// keys React elements and its click selection off this, both of which break on
// duplicates. Treat the result as opaque — see the `Intervention.id` comment in
// types/weekOverview.ts for why it must not be parsed.
//
// `planIndex` assumes the plan array's order is stable for as long as a week
// stays loaded. It only has to hold within one loaded result: the sole consumer
// is the calendar, and the worst case if a refetch returned the same assignments
// in a different order is that the open block loses its selection highlight.
// The solver returns a plan per job and the FE refetches whole weeks rather than
// patching them, so this is safe today — but a caller that starts merging
// assignments into an existing plan would need a real per-occurrence key from
// the backend instead.
function blockIdFor(
  plannedInterventionId: string,
  assignedDate: string,
  assignedStartTime: string,
  planIndex: number
): string {
  return `${plannedInterventionId}:${assignedDate}:${assignedStartTime}:${planIndex}`;
}

// Joins each scheduled assignment with its intervention definition from the
// solver input. Assignments outside the displayed week, missing their core
// fields, or referencing an unknown intervention are skipped rather than
// rendered half-empty.
function buildInterventions(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>
): Intervention[] {
  const interventionById = new Map<string, SolverIntervention>();
  for (const definition of result.input?.interventions ?? []) {
    if (definition.id) interventionById.set(definition.id, definition);
  }

  const interventions: Intervention[] = [];
  for (const [planIndex, assignment] of (result.plan ?? []).entries()) {
    // Room is intentionally not required — internal sessions such as the
    // solver-injected lunch breaks are placed without one.
    const { plannedInterventionId, assignedDate, assignedStartTime } = assignment;
    if (!plannedInterventionId || !assignedDate || !assignedStartTime) continue;

    const definition = interventionById.get(plannedInterventionId);
    const day = dayByDate.get(assignedDate);
    if (!definition || !day) continue;

    const sessionType = sessionTypeFor(definition);
    if (!sessionType) continue;

    const startMinutes = parseTimeToMinutes(assignedStartTime);
    const staffIds = (assignment.staffOccupancies ?? [])
      .map((occupancy) => occupancy.staffId)
      .filter((staffId): staffId is string => Boolean(staffId));

    interventions.push({
      id: blockIdFor(plannedInterventionId, assignedDate, assignedStartTime, planIndex),
      title: definition.name ?? '',
      sessionType,
      programLabel: null,
      day,
      startMinutes,
      endMinutes: startMinutes + (definition.durationMinutes ?? 0),
      roomId: assignment.assignedRoomId ?? '',
      staffIds: [...new Set(staffIds)],
      patientIds: definition.patientIds ?? [],
      requiredSkillIds: requiredSkillIdsFor(definition),
      notes: null
    });
  }
  return interventions;
}

// Interventions the solver could not place, merged from its two reporting
// channels: diagnostics.unscheduledDiagnostics (with the feasible-slot count)
// and the UNSCHEDULED_COUNT satisfaction issues. Each id is joined with its
// definition from the input snapshot; an id without a definition still renders
// (title falls back to the raw id) rather than disappearing from the report.
function buildUnscheduledInterventions(result: SchedulingResult): UnscheduledIntervention[] {
  const feasibleCountById = new Map<string, number | null>();
  for (const diagnostic of result.diagnostics?.unscheduledDiagnostics ?? []) {
    if (!diagnostic.interventionId) continue;
    feasibleCountById.set(diagnostic.interventionId, diagnostic.feasibleCandidateCount ?? null);
  }

  const unscheduledIds = new Set(feasibleCountById.keys());
  for (const issue of result.issues ?? []) {
    if (issue.criterionGap !== SatisfactionIssueCriterionGapEnum.UnscheduledCount) continue;
    if (issue.interventionId) unscheduledIds.add(issue.interventionId);
  }

  const interventionById = new Map<string, SolverIntervention>();
  for (const definition of result.input?.interventions ?? []) {
    if (definition.id) interventionById.set(definition.id, definition);
  }

  return [...unscheduledIds]
    .map((id): UnscheduledIntervention => {
      const definition = interventionById.get(id);
      return {
        id,
        title: definition?.name ?? id,
        sessionType: definition ? sessionTypeFor(definition) : null,
        patientIds: definition?.patientIds ?? [],
        requiredSkillIds: definition ? requiredSkillIdsFor(definition) : [],
        durationMinutes: definition?.durationMinutes ?? null,
        feasibleCandidateCount: feasibleCountById.get(id) ?? null
      };
    })
    .sort((a, b) => a.title.localeCompare(b.title));
}

export interface WeekUnscheduledSummary {
  readonly interventions: readonly UnscheduledIntervention[];
  // Patient display names from the solve's own input snapshot, so the list can
  // name people without a second round-trip to the patients endpoint.
  readonly patientNameById: ReadonlyMap<string, string>;
}

// Just the unscheduled part of a solve. The year plan's badge counts come from
// the week statuses, so this runs for the one week a planner opens; mapping that
// week to full WeekData would build calendar layouts, per-person day loads and
// KPIs that the dialog never reads.
export function buildWeekUnscheduledSummary(
  response: SchedulingJobResultResponse
): WeekUnscheduledSummary {
  const result = response.result;
  const patientNameById = new Map<string, string>();
  for (const patient of result.input?.patients ?? []) {
    if (patient.id) patientNameById.set(patient.id, patient.displayName ?? patient.id);
  }
  return { interventions: buildUnscheduledInterventions(result), patientNameById };
}

// The roster facts needed to score one staff member's week, resolved once per
// person so the per-day work stays a lookup.
type StaffRoster = {
  staffId: string;
  workingHours: Staff['workingHours'];
  holidayDates: Set<string>;
};

// Booked minutes per staff member per weekday, keyed `${staffId}|${day}`.
// Occupancies span preparation through post-processing, so a person can be
// booked beyond the intervention times themselves.
function buildBookedMinutesByStaffDay(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>
): Map<string, number> {
  const bookedMinutesByStaffDay = new Map<string, number>();

  for (const assignment of result.plan ?? []) {
    const day = assignment.assignedDate ? dayByDate.get(assignment.assignedDate) : undefined;
    if (!day) continue;
    for (const occupancy of assignment.staffOccupancies ?? []) {
      const minutes = occupancyMinutes(occupancy);
      if (!occupancy.staffId || minutes === null) continue;
      const key = `${occupancy.staffId}|${day}`;
      const booked = bookedMinutesByStaffDay.get(key) ?? 0;
      bookedMinutesByStaffDay.set(key, booked + minutes);
    }
  }

  return bookedMinutesByStaffDay;
}

// One person's load on one day, or null when the day should not appear at all:
// a day the location was closed and this person does not work.
function buildStaffDayLoad(
  roster: StaffRoster,
  day: Weekday,
  date: string,
  bookedMinutes: number,
  openDays: ReadonlySet<Weekday>
): PersonDayLoad | null {
  const workingWindow = workingWindowFor(roster.workingHours, date);
  if (!workingWindow && !openDays.has(day)) return null;

  const isAbsent = !workingWindow || roster.holidayDates.has(date);
  const availableHours = isAbsent || !workingWindow ? 0 : windowDurationHours(workingWindow);
  const bookedHours = bookedMinutes / 60;
  const utilizationPercent =
    availableHours > 0
      ? Math.min(MAX_UTILIZATION_PERCENT, Math.round((bookedHours / availableHours) * 100))
      : 0;

  return {
    staffId: roster.staffId,
    day,
    bookedHours: roundToTenth(bookedHours),
    availableHours: roundToTenth(availableHours),
    utilizationPercent,
    isAbsent
  };
}

// One load entry per person per day: available hours from the roster's working
// window, booked hours from the person's scheduled occupancies (prep and
// post-processing included). A day the location was closed gets a load only when
// someone has working hours there, which a backend snapshot never has, since it
// clips working hours to the opening hours. The heatmap renders such a day
// closed either way, through closedDays.
function buildPersonDayLoads(
  result: SchedulingResult,
  dayByDate: Map<string, Weekday>,
  openDays: ReadonlySet<Weekday>
): PersonDayLoad[] {
  const bookedMinutesByStaffDay = buildBookedMinutesByStaffDay(result, dayByDate);
  const dateByDay = new Map([...dayByDate].map(([date, day]) => [day, date]));
  const loads: PersonDayLoad[] = [];

  for (const staff of result.input?.staff ?? []) {
    if (!staff.id) continue;
    const roster: StaffRoster = {
      staffId: staff.id,
      workingHours: staff.workingHours,
      holidayDates: new Set(staff.holidayDates ?? [])
    };

    for (const day of WEEKDAYS_ORDER) {
      const date = dateByDay.get(day);
      if (!date) continue;

      const bookedMinutes = bookedMinutesByStaffDay.get(`${roster.staffId}|${day}`) ?? 0;
      const load = buildStaffDayLoad(roster, day, date, bookedMinutes, openDays);
      if (load) loads.push(load);
    }
  }

  return loads;
}

// Dates a closure takes whole. Closed-day bookings travel apart from the opening
// hours, as centreClosures: an entry without times closes its date, one with
// times is the part of the date left open.
function wholeDayClosureDates(input: SchedulingInput): Set<string> {
  const dates = new Set<string>();
  for (const closure of input.horizon?.centreClosures ?? []) {
    if (closure.date && (!closure.startTime || !closure.endTime)) dates.add(closure.date);
  }
  return dates;
}

// The days of the week the location was open for this solve: the dates of the
// input snapshot's opening hours, which the backend builds from the location's
// opening days, less the dates a closure takes whole. Read off the snapshot
// rather than the location's current settings, so a later edit does not redraw a
// plan solved before it. The backend always sends opening hours, so a snapshot
// without them is malformed: it is drawn with the default days rather than
// failing the page, and reported. So are dates from outside the displayed week,
// which would otherwise draw the week as closed without a trace.
function buildOpenDays(
  input: SchedulingInput,
  dayByDate: Map<string, Weekday>,
  year: number,
  week: number
): Set<Weekday> {
  const openingHours = input.horizon?.centreOpeningHours;
  if (!openingHours) {
    console.error(
      `Scheduling result for week ${week}/${year} carries no opening hours; drawing the default opening days`
    );
    return new Set(DEFAULT_OPENING_WEEKDAYS);
  }
  const closedDates = wholeDayClosureDates(input);
  const openDays = new Set<Weekday>();
  const datesOutsideWeek: string[] = [];
  for (const date of Object.keys(openingHours)) {
    const day = dayByDate.get(date);
    if (!day) datesOutsideWeek.push(date);
    else if (!closedDates.has(date)) openDays.add(day);
  }
  if (datesOutsideWeek.length > 0) {
    console.error(
      `Scheduling result for week ${week}/${year} has opening hours outside the week:`,
      datesOutsideWeek
    );
  }
  return openDays;
}

function buildKpis(
  input: SchedulingInput,
  interventions: Intervention[],
  loads: PersonDayLoad[]
): WeekKpiSummary {
  let interventionHours = 0;
  let otherBookingHours = 0;

  // Patient-facing sessions (individual + group) count as interventions;
  // everything else that is real work — meetings and other internal bookings —
  // counts as other bookings. Lunch breaks are neither work nor white time, so
  // they stay out of the task distribution entirely, mirroring how the occupancy
  // side ignores them.
  for (const intervention of interventions) {
    const hours = (intervention.endMinutes - intervention.startMinutes) / 60;
    if (intervention.sessionType === 'INDIVIDUAL' || intervention.sessionType === 'GROUP') {
      interventionHours += hours;
    } else if (intervention.sessionType !== 'LUNCH') {
      otherBookingHours += hours;
    }
  }

  let totalAvailable = 0;
  let totalBooked = 0;
  for (const load of loads) {
    totalAvailable += load.availableHours;
    totalBooked += load.bookedHours;
  }

  const patientCount = input.patients?.length ?? 0;

  return {
    occupancyBookedHours: Math.round(totalBooked),
    occupancyAvailableHours: Math.round(totalAvailable),
    taskDistribution: {
      interventionHours: Math.round(interventionHours),
      // Real staff-leave data is not part of the scheduling result yet
      // (arrives via REH-200); until then vacation stays empty rather than
      // guessing it from roster gaps, which would misreport part-time gaps.
      vacationHours: 0,
      otherBookingHours: Math.round(otherBookingHours),
      whiteTimeHours: Math.round(Math.max(0, totalAvailable - totalBooked))
    },
    staffCount: input.staff?.length ?? 0,
    interventionCount: interventions.filter(
      (i) => i.sessionType !== 'MEETING' && i.sessionType !== 'LUNCH'
    ).length,
    patientsAdmitted: patientCount,
    patientsTotal: patientCount
  };
}

// Maps the newest completed solver result onto the week overview's view model.
// The result embeds its full input snapshot (rooms, staff, patients,
// intervention definitions), which covers the rooms, staff, patients and
// interventions the week renders. Two things come from outside it: the location
// name, and the staff-profession register, which decides which skill rows the
// heatmap has at all — every non-archived "Show in KPI" competence gets a row,
// and the solver output only fills those rows in (see buildSkills).
export function mapSchedulingResultToWeekData(
  response: SchedulingJobResultResponse,
  year: number,
  week: number,
  locationName: string,
  professions: StaffProfessionResponse[]
): PlannedWeekData {
  const result = response.result;
  const input = result.input ?? {};

  // Date-key → weekday lookup for the displayed week; assignments on other
  // dates (defensive — the job is per-week) fall outside and are dropped.
  const dayByDate = new Map<string, Weekday>(
    WEEKDAYS_ORDER.map((day) => [toIsoDateKey(getDateForWeekday(year, week, day)), day])
  );

  const openDays = buildOpenDays(input, dayByDate, year, week);
  const interventions = buildInterventions(result, dayByDate);
  const personDayLoads = buildPersonDayLoads(result, dayByDate, openDays);

  return {
    ...buildWeekShell(year, week, locationName),
    isPlanned: true,
    lastModifiedDate: response.finishedAt,
    skills: buildSkills(input, professions),
    staff: (input.staff ?? [])
      .map((staff) => ({
        id: staff.id ?? '',
        name: staff.displayName ?? '',
        skillIds: staff.staffProfessionIds ?? []
      }))
      .filter((staff) => staff.id !== '')
      .sort((a, b) => a.name.localeCompare(b.name)),
    rooms: (input.rooms ?? [])
      .map((room) => ({ id: room.id ?? '', name: room.displayName ?? '' }))
      .filter((room) => room.id !== ''),
    patients: (input.patients ?? [])
      .map((patient) => ({ id: patient.id ?? '', name: patient.displayName ?? '' }))
      .filter((patient) => patient.id !== ''),
    closedDays: WEEKDAYS_ORDER.filter((day) => !openDays.has(day)),
    personDayLoads,
    interventions,
    unscheduledInterventions: buildUnscheduledInterventions(result),
    kpis: buildKpis(input, interventions, personDayLoads)
  };
}
